﻿import { useEffect, useRef } from "react";
import Button from "./Button.jsx";

/** Glassmorphic modal — DESIGN.md §2 allows translucent surfaces for floating UI. */
export default function Modal({ open, onClose, title, children, footer, size = "md" }) {
  const panelRef = useRef(null);

  /**
   * `onClose` is almost always an inline arrow (`onClose={() => setX(null)}`),
   * so it gets a new identity on every parent render — and a parent re-renders
   * on every keystroke in a controlled input. The focus effect below must NOT
   * depend on it, or typing would yank focus out of the field and onto the
   * dialog panel after each character. The listener reads the latest callback
   * from a ref instead, so the effect only ever runs on the open transition.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl" };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default animate-fade-in bg-inverse-surface/40 backdrop-blur-sm motion-reduce:animate-none"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`glass-card relative w-full ${widths[size]} animate-scale-in rounded-2xl shadow-float outline-none motion-reduce:animate-none`}
      >
        <div className="flex items-start justify-between gap-4 px-8 pt-8">
          <h2 className="font-headline text-2xl font-extrabold tracking-tight text-on-surface">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-all duration-200 hover:bg-surface-container active:scale-90"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div className="scrollbar-swastik max-h-[65vh] overflow-y-auto px-8 py-6">
          {children}
        </div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-3 px-8 pb-8">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = true,
  loading = false,
  onConfirm,
  onClose,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-on-surface-variant">{message}</p>
    </Modal>
  );
}
